import React, { useEffect, useState } from 'react';
import { Box, Grid, Paper, Table, TableBody, TableRow, TableCell, TableContainer, TableHead, Typography, Chip, Tooltip, Divider, LinearProgress, Link } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Crumbs, DataRow, DateDisplay, MonoText, Title, TraxxButton } from 'app/components';
import { API } from 'app/services';
import { snakeToTitle, useAsyncTask, useRouter } from 'app/utils';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { Link as BrowserLink } from 'react-router-dom';
import { DownloadRegtankPreview } from './components';
import { isObject } from 'validate.js';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3, 3, 8) },
  table: { marginTop: 14, borderRadius: 5 },
  title: { marginTop: 15, textDecoration: 'underline' },
  head: { fontWeight: 'bold', maxWidth: 200 },
  subtitle: { opacity: '70%' },
  tag: { margin: '2px' },
  tooltip: { fontSize: '1rem' },
}));

// ---------------------------------------------------------------------------
// Keyword chip config
// ---------------------------------------------------------------------------
const KEYWORD_CONFIG = {
  PEP: { label: 'Political Exposed Person', color: '#1976d2' },
  SAN: { label: 'Sanction', color: '#d32f2f' },
  SI: { label: 'Special Interest', color: '#7b1fa2' },
  AM: { label: 'Adverse Media', color: '#ed6c02' },
  FR: { label: 'Financial Regulator', color: '#2e7d32' },
  LF: { label: 'Law Enforcement', color: '#d32f2f' },
  POI: { label: 'Profile of Interest', color: '#607d8b' },
  OOL: { label: 'Other Official Lists', color: '#00acc1' },
};

const KeywordChip = ({ code, classes }) => {
  const config = KEYWORD_CONFIG[code] || {};
  return (
    <Tooltip title={config.label || code} classes={{ tooltip: classes.tooltip }}>
      <Chip label={code} size="small" className={classes.tag} style={{ backgroundColor: config.color || '#9e9e9e', color: 'white' }} />
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// Reusable connections table (linked individuals / linked businesses / connections)
// ---------------------------------------------------------------------------
const ConnectionsTable = ({ title, rows, classes }) => {
  if (!rows?.length) return null;
  return (
    <Grid item xs={12}>
      <TableContainer component={Paper} className={classes.table}>
        <Title prefix={title} className={classes.title} />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Name</strong>
              </TableCell>
              <TableCell>
                <strong>URL</strong>
              </TableCell>
              <TableCell>
                <strong>Association</strong>
              </TableCell>
              <TableCell>
                <strong>Keywords</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((p, i) => (
              <TableRow key={i} hover>
                <TableCell>{p.name || '-'}</TableCell>
                <TableCell>
                  {p.url ? (
                    <a href={p.url} target="_blank" rel="noopener noreferrer">
                      {p.url}
                    </a>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>{p.associations || '-'}</TableCell>
                <TableCell>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {p.keywords?.length > 0 ? p.keywords.map((kw, j) => <KeywordChip key={j} code={kw} classes={classes} />) : '-'}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
};

// ---------------------------------------------------------------------------
// Heading detection for profile notes
// ---------------------------------------------------------------------------
const isHeading = (p) => {
  if (!p) return false;
  const clean = p.trim();
  if (!clean || clean.length > 120) return false;
  if (clean === clean.toUpperCase()) return true;
  return clean.split(' ').some((w) => w.length > 3 && w === w.toUpperCase());
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
let mounted = false;

function MatchDetailRegtank({ routeType = 'account' }) {
  const classes = useStyles();
  const router = useRouter();
  const { params } = router.match;
  const { enqueueSnackbar } = useSnackbar();

  const access = useSelector((state) => state.profile.access);
  const loading = useSelector((state) => state.layout.loadingTasks);

  const [errorCatcher] = useAsyncTask('getScreening');
  const [runUnresolve] = useAsyncTask('unresolve');

  const [matching, setMatching] = useState(null);
  const isOrganisation = matching?.screen?.type === 'organisation';
  const [matchList, setMatchListing] = useState();
  const [matchRawResult, setMatchRawResult] = useState(null);
  const [printPreview, setPrintPreview] = useState(false);

  const { screening_id, match_id } = params;

  useEffect(() => {
    mounted = true;
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (access?.token) getMatchDetail();
  }, [access]);

  // -------------------------------------------------------------------------
  const getMatchDetail = () => {
    if (!screening_id || !match_id) return enqueueSnackbar('invalid id', { variant: 'error' });

    errorCatcher(async () => {
      const loader = routeType === 'account' ? API.Screening.get_match_detail : API.Admin.get_match;

      const { model } = await loader(screening_id, match_id);
      if (!mounted) return;

      setMatching(model);

      const { content, aml_types, lists, media, peps, sanctions } = model.screen_result_raw || {};

      const parsed = content ? JSON.parse(content) : {};
      const match = parsed.match || {};
      const matchDetails = parsed.matchDetails || {};
      const parsedPeps = peps ? JSON.parse(peps) : {};
      const parsedLists = lists ? JSON.parse(lists) : [];
      const parsedMedia = media ? JSON.parse(media) : [];
      const parsedSanctions = sanctions ? JSON.parse(sanctions) : [];
      const parsedAmlTypes = aml_types ? JSON.parse(aml_types) : [];

      const rawResult = {
        detail: {
          profile: {
            matching_name: match.name,
            person: {
              names: [{ name: match.name }],
              aliases: parsedPeps.aliases || [],
              nationalities: match.nationality ? [match.nationality] : [],
              dates_of_birth: match.dayOfBirth ? [{ value: `${match.dayOfBirth}-${match.monthOfBirth}-${match.yearOfBirth}`, source: 'Regtank' }] : [],
              addresses: parsedPeps.addresses || [],
              deceased: parsedPeps.deceased || false,
              reportedAllegation: parsedPeps.reportedAllegation || null,
              associates: (matchDetails.connections || []).map((c) => ({
                type: c.associations || 'Associate',
                name: c.name,
                keywords: c.keywords || [],
              })),
              profileNotes: parsedPeps.profileNotes || [],
            },
          },
        },
        media: parsedMedia,
        lists: parsedLists,
        peps: parsedPeps,
        sanctions: parsedSanctions,
        aml_types: parsedAmlTypes,
      };

      setMatchRawResult(rawResult);
    });
  };

  // -------------------------------------------------------------------------
  const getScreenDetailPath = (id) => (routeType === 'admin' ? `/admin/screenings/${id}/detail` : `/screenings/${id}/detail`);

  const getCaseDetailPath = (id) => (routeType === 'admin' ? `/admin/screenings/cases/${id}/detail` : `/screenings/cases/${id}/detail`);

  const unResolve = () => {
    runUnresolve(async () => {
      await API.Users.unresolve_matches(screening_id, { resolve_ids: [match_id] });
      enqueueSnackbar('Match unresolved', { variant: 'success' });
      getMatchDetail();
    });
  };

  // -------------------------------------------------------------------------
  return (
    <Box className={classes.root}>
      {!printPreview && (
        <>
          {/* BREADCRUMBS */}
          <Crumbs
            current="Detail"
            links={[
              {
                text: 'Screening',
                href: routeType === 'admin' ? '/admin/screenings' : '/screenings',
                key: 'screening',
              },
              {
                text: params.screening_id,
                href: getScreenDetailPath(params.screening_id),
                key: 'screeningId',
              },
            ]}
          />

          <Title
            prefix="Match detail"
            floatButton={
              matching?.profile_retrieved_at && {
                onClick: () => setPrintPreview(true),
                text: 'Preview print document',
              }
            }>
            {matching && 'Powered by Regtank'}
          </Title>

          <Grid spacing={2} container>
            {/* ── LEFT PANEL: match metadata ─────────────────────────────── */}
            <Grid item md={6} xs={12}>
              <TableContainer component={Paper} className={classes.table}>
                {loading.getScreening && <LinearProgress />}
                {!loading.getScreening && matching && (
                  <Table>
                    <TableBody>
                      <DataRow name="Name" value={matching.name} />

                      {(matching.screen?.type === 'simple' || matching.screen?.type === 'passport') && <DataRow name="Gender" value={snakeToTitle(matching.gender || '')} />}

                      <DataRow name="Created date" value={<DateDisplay date={matching.created_at} />} />
                      <DataRow name="Match ID" value={<MonoText>{matching.reference}</MonoText>} />

                      <DataRow
                        name="Screening ID"
                        value={
                          <Link underline="always" component={BrowserLink} to={getScreenDetailPath(matching.screen?.id)}>
                            <MonoText>{matching.screen?.reference}</MonoText>
                          </Link>
                        }
                      />

                      {matching.screen?.case && (
                        <DataRow
                          name="Case ID"
                          value={
                            <Link underline="always" component={BrowserLink} to={getCaseDetailPath(matching.screen.case.id)}>
                              <MonoText>{matching.screen.case.reference}</MonoText>
                            </Link>
                          }
                        />
                      )}

                      <DataRow name="Resolved" value={<DateDisplay date={matching.resolved_at} />} />

                      {matching.resolved_reason && matching.resolved_at && <DataRow name="Resolved reason" value={matching.resolved_reason} />}

                      {matching.resolved_at && routeType !== 'admin' && (
                        <DataRow
                          name=""
                          value={
                            <TraxxButton loading={loading.unresolve} onSubmit={unResolve}>
                              <Typography>Unresolve</Typography>
                            </TraxxButton>
                          }
                        />
                      )}

                      {matching.profile_retrieved_at && <DataRow name="Profile retrieved date" value={<DateDisplay date={matching.profile_retrieved_at} />} />}
                    </TableBody>
                  </Table>
                )}
              </TableContainer>
            </Grid>

            {!isOrganisation && (
              <>
                <Grid item md={6} xs={12}>
                  <Paper className={classes.table} style={{ overflow: 'hidden' }}>
                    <Title prefix="Key Information" className={classes.title} />

                    {/* ── Header: avatar + name + role + PEP badge ── */}
                    {matchRawResult && (
                      <>
                        <Box display="flex" alignItems="center" style={{ padding: '16px', borderBottom: '1px solid rgba(0,0,0,0.08)', gap: 12 }}>
                          <Box
                            style={{
                              width: 44,
                              height: 44,
                              borderRadius: '50%',
                              background: '#e3f2fd',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}>
                            <Typography style={{ fontWeight: 600, fontSize: 15, color: '#1976d2' }}>
                              {matchRawResult?.detail?.profile?.matching_name
                                ?.split(/[\s,]+/)
                                .filter(Boolean)
                                .map((w) => w[0])
                                .slice(0, 2)
                                .join('')
                                .toUpperCase()}
                            </Typography>
                          </Box>
                          <Box flex={1}>
                            <Typography style={{ fontWeight: 600, fontSize: 15 }}>{matchRawResult?.detail?.profile?.matching_name}</Typography>
                            <Typography style={{ fontSize: 13, opacity: 0.6 }}>{matchRawResult?.lists?.referenceList?.[0]?.primaryOccupation?.title || matchRawResult?.lists?.referenceList?.[0]?.subCategory || ''}</Typography>
                          </Box>
                          <Box display="flex" flexWrap="wrap" justifyContent="flex-end" style={{ gap: 4 }}>
                            {matchRawResult?.aml_types?.map((aml, i) => (
                              <KeywordChip key={i} code={aml} classes={classes} />
                            ))}
                          </Box>
                        </Box>

                        {/* ── Identity grid ── */}
                        <Box style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                          <Typography style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', opacity: 0.45, textTransform: 'uppercase', marginBottom: 10 }}>Identity</Typography>

                          {matchRawResult?.detail?.profile?.person?.aliases?.length > 0 && (
                            <Box mb={1.5}>
                              <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 6 }}>Aliases</Typography>
                              <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                                {matchRawResult.detail.profile.person.aliases.map((alias, i) => (
                                  <Box
                                    key={i}
                                    style={{
                                      background: 'rgba(0,0,0,0.04)',
                                      border: '0.5px solid rgba(0,0,0,0.1)',
                                      borderRadius: 4,
                                      padding: '2px 8px',
                                      fontSize: 12,
                                      color: 'inherit',
                                      whiteSpace: 'nowrap',
                                    }}>
                                    {alias.trim()}
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}

                          <Box display="flex" flexWrap="wrap" style={{ gap: '12px 24px' }}>
                            {[
                              { label: 'Gender', value: matchRawResult?.lists?.gender },
                              { label: 'Nationality', value: matchRawResult?.lists?.nationality ? `${matchRawResult.lists.nationality}${matchRawResult.lists.nationalityCode ? ` (${matchRawResult.lists.nationalityCode})` : ''}` : null },
                              { label: 'Jurisdiction', value: matchRawResult?.peps?.jurisdiction },
                              { label: 'Country of residence', value: matchRawResult?.lists?.countryOfResidence },
                              { label: 'Place of birth', value: matchRawResult?.lists?.placeOfBirth },
                              {
                                label: 'Deceased',
                                value: matchRawResult?.peps?.deceased
                                  ? `Yes${
                                      matchRawResult.peps?.monthOfDeath || matchRawResult.peps?.yearOfDeath || matchRawResult.peps?.dayOfDeath
                                        ? ` (${[matchRawResult.peps?.dayOfDeath, matchRawResult.peps?.monthOfDeath, matchRawResult.peps?.yearOfDeath].filter(Boolean).join('/')})`
                                        : ''
                                    }`
                                  : 'No',
                              },
                            ].map(({ label, value }) => (
                              <Box key={label} style={{ minWidth: 100 }}>
                                <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 2 }}>{label}</Typography>
                                <Typography style={{ fontSize: 13, opacity: value ? 1 : 0.35 }}>{value || '—'}</Typography>
                              </Box>
                            ))}
                          </Box>

                          {matchRawResult?.detail?.profile?.person?.addresses?.length > 0 && (
                            <Box mt={1.5}>
                              <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 2 }}>Address</Typography>
                              {matchRawResult.detail.profile.person.addresses.map((addr, i) => (
                                <Typography key={i} style={{ fontSize: 13 }}>
                                  {[addr.city, addr.stateOrRegion, addr.countryOrTerritory].filter(Boolean).join(', ')}
                                </Typography>
                              ))}
                            </Box>
                          )}

                          {matchRawResult?.detail?.profile?.person?.reportedAllegation && (
                            <Box mt={1.5}>
                              <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 2 }}>Reported allegation</Typography>
                              <Typography style={{ fontSize: 13 }}>{matchRawResult.detail.profile.person.reportedAllegation}</Typography>
                            </Box>
                          )}
                        </Box>

                        {/* ── Date of birth ── */}
                        <Box style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                          <Typography style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', opacity: 0.45, textTransform: 'uppercase', marginBottom: 10 }}>Date of birth</Typography>
                          <Box display="flex" style={{ gap: '12px 40px', flexWrap: 'wrap' }}>
                            <Box>
                              <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 2 }}>Recorded DOB</Typography>
                              <Typography style={{ fontSize: 13, opacity: matchRawResult?.detail?.profile?.person?.dates_of_birth?.length > 0 ? 1 : 0.35 }}>
                                {matchRawResult?.detail?.profile?.person?.dates_of_birth?.length > 0 ? matchRawResult.detail.profile.person.dates_of_birth.map((d) => d.value).join(', ') : '—'}
                              </Typography>
                            </Box>
                            {matchRawResult?.peps?.otherRecordedDatesOfBirth?.length > 0 && (
                              <Box>
                                <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 2 }}>Other recorded DOB</Typography>
                                <Typography style={{ fontSize: 13 }}>{matchRawResult.peps.otherRecordedDatesOfBirth.map((d) => [d.day, d.month, d.year].filter(Boolean).join('/')).join('  ·  ')}</Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {matchRawResult.lists.images?.length > 0 && (
                          <>
                            <TableRow>
                              <TableCell>
                                <Typography className={classes.head}>Images</Typography>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                  {matchRawResult.lists.images
                                    .filter((img) => img && !img.includes('47news.jp'))
                                    .map((img, i) => (
                                      <Box key={i} width={120} height={120} style={{ borderRadius: 6, overflow: 'hidden', background: '#f5f5f5' }}>
                                        <img
                                          src={img}
                                          alt={`Image ${i + 1}`}
                                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      </Box>
                                    ))}
                                </Box>
                              </TableCell>
                            </TableRow>
                          </>
                        )}
                        {matchRawResult?.lists?.referenceList?.length > 0 && (
                          <Box style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                            <Typography style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', opacity: 0.45, textTransform: 'uppercase', marginBottom: 10 }}>Risk Classifications</Typography>
                            <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
                              {matchRawResult.lists.referenceList.map((ref, i) => {
                                const listItems = [
                                  ...(ref.sanctions || []).map((s) => ({ ...s, type: 'sanctions' })),
                                  ...(ref.otherOfficialList || []).map((o) => ({ ...o, type: 'ool' })),
                                  ...(ref.specialInterestPersonList || []).map((s) => ({ ...s, type: 'si' })),
                                ];

                                return (
                                  <Box
                                    key={i}
                                    style={{
                                      background: 'rgba(0,0,0,0.02)',
                                      border: '0.5px solid rgba(0,0,0,0.08)',
                                      borderRadius: 6,
                                      padding: '10px 12px',
                                    }}>
                                    {/* Header row: chip + category + status */}
                                    <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: listItems.length > 0 ? 8 : 0 }}>
                                      <KeywordChip code={ref.keyword} classes={classes} />
                                      <Typography style={{ fontSize: 13, flex: 1 }}>{ref.category}</Typography>
                                      <Box
                                        style={{
                                          fontSize: 11,
                                          fontWeight: 600,
                                          padding: '2px 8px',
                                          borderRadius: 4,
                                          background: ref.status === 'ACTIVE' ? 'rgba(76,175,80,0.1)' : 'rgba(0,0,0,0.06)',
                                          color: ref.status === 'ACTIVE' ? '#2e7d32' : '#666',
                                        }}>
                                        {ref.status}
                                      </Box>
                                    </Box>

                                    {/* Sub-category */}
                                    {ref.subCategory && <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 6 }}>{ref.subCategory}</Typography>}

                                    {/* Expanded list entries */}
                                    {listItems.length > 0 && (
                                      <Box style={{ borderTop: '0.5px solid rgba(0,0,0,0.06)', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {listItems.map((item, j) => (
                                          <Box key={j} display="flex" alignItems="flex-start" style={{ gap: 8 }}>
                                            <Box style={{ width: 4, height: 4, borderRadius: '50%', background: '#999', marginTop: 6, flexShrink: 0 }} />
                                            <Box>
                                              <Typography style={{ fontSize: 12, fontWeight: 600 }}>{item.name}</Typography>

                                              {item.from?.year && (
                                                <Typography style={{ fontSize: 11, opacity: 0.5 }}>
                                                  Since {[item.from.day, item.from.month, item.from.year].filter(Boolean).join('/')}
                                                  {item.to ? ` – ${[item.to.day, item.to.month, item.to.year].filter(Boolean).join('/')}` : ' – present'}
                                                </Typography>
                                              )}
                                              {item.sources?.length > 0 && (
                                                <Box mt={0.5}>
                                                  {item.sources.map((src, k) => {
                                                    const isString = typeof src === 'string';
                                                    const url = isString ? src : src.sourceName;
                                                    const label = isString ? src : [src.sourceName, src.sourceDate, src.sourceLanguage].filter(Boolean).join(' · ');
                                                    return url ? (
                                                      <a key={k} href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, display: 'block', wordBreak: 'break-all', color: '#1976d2' }}>
                                                        {label}
                                                      </a>
                                                    ) : (
                                                      <span key={k} style={{ fontSize: 11, display: 'block', color: '#666' }}>
                                                        {label}
                                                      </span>
                                                    );
                                                  })}
                                                </Box>
                                              )}
                                            </Box>
                                          </Box>
                                        ))}
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        )}
                        {/* ── Roles ── */}
                        {matchRawResult?.lists?.referenceList?.length > 0 && (
                          <Box style={{ padding: '14px 16px' }}>
                            <Typography style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', opacity: 0.45, textTransform: 'uppercase', marginBottom: 10 }}>Roles</Typography>
                            <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
                              {matchRawResult.lists.referenceList.map((ref, i) => {
                                const roles = [...(ref.primaryOccupation ? [{ ...ref.primaryOccupation, current: true }] : []), ...(ref.previousRoles || []).map((r) => ({ ...r, current: false }))];
                                return roles.map((role, j) => (
                                  <Box key={`${i}-${j}`} display="flex" alignItems="flex-start" style={{ gap: 10 }}>
                                    <Box
                                      style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        marginTop: 4,
                                        flexShrink: 0,
                                        background: role.current ? '#4caf50' : '#bdbdbd',
                                        border: `2px solid ${role.current ? '#a5d6a7' : '#e0e0e0'}`,
                                      }}
                                    />
                                    <Box>
                                      <Typography style={{ fontSize: 13, fontWeight: role.current ? 600 : 400 }}>{role.title}</Typography>
                                      <Typography style={{ fontSize: 12, opacity: 0.55 }}>
                                        {[role.from?.month, role.from?.year].filter(Boolean).join('/')}
                                        {role.to ? ` – ${[role.to.month, role.to.year].filter(Boolean).join('/')}` : ' – present'}
                                        {role.category ? `  ·  ${role.category}` : ''}
                                      </Typography>
                                    </Box>
                                  </Box>
                                ));
                              })}
                            </Box>
                          </Box>
                        )}
                      </>
                    )}
                  </Paper>
                </Grid>
                {(matchRawResult?.lists?.connections?.length > 0 || matchRawResult?.lists?.linkedIndividuals?.length > 0 || matchRawResult?.lists?.linkedBusinesses?.length > 0) && (
                  <Grid item xs={12}>
                    <Paper className={classes.table} style={{ overflow: 'hidden' }}>
                      <Title prefix="Connections & Associates" className={classes.title} />
                      <Grid container spacing={2} style={{ padding: '14px 16px' }}>
                        {matchRawResult?.lists?.connections?.length > 0 && (
                          <Grid item xs={12}>
                            <ConnectionsTable title="Connections" rows={matchRawResult.lists.connections} classes={classes} />
                          </Grid>
                        )}
                        {matchRawResult?.lists?.linkedIndividuals?.length > 0 && (
                          <Grid item xs={12}>
                            <ConnectionsTable title="Linked Individuals" rows={matchRawResult.lists.linkedIndividuals} classes={classes} />
                          </Grid>
                        )}
                        {matchRawResult?.lists?.linkedBusinesses?.length > 0 && (
                          <Grid item xs={12}>
                            <ConnectionsTable title="Linked Businesses" rows={matchRawResult.lists.linkedBusinesses} classes={classes} />
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>
                )}
                {/* ── MEDIA ──────────────────────────────────────────────────── */}
                {matchRawResult?.media?.length > 0 && (
                  <Grid item xs={12}>
                    <TableContainer component={Paper} className={classes.table}>
                      <Title prefix="Media" className={classes.title} />
                      <Table>
                        <TableBody>
                          {matchRawResult.media.map((item, i) => (
                            <TableRow key={i}>
                              <TableCell>
                                <Link href={item.externalLink} target="_blank" rel="noopener noreferrer">
                                  {item.title || item.externalLink}
                                </Link>
                                {item.title && (
                                  <Typography variant="caption" display="block" style={{ color: 'gray' }}>
                                    {item.externalLink}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}
                {/* ── PROFILE NOTES ──────────────────────────────────────────── */}
                {matchRawResult?.detail?.profile?.person?.profileNotes?.length > 0 && (
                  <Grid item xs={12}>
                    <TableContainer component={Paper} className={classes.table}>
                      <Title prefix="Profile Notes" className={classes.title} />
                      <Table>
                        <TableBody>
                          {matchRawResult.detail.profile.person.profileNotes.map((note, i) => {
                            const paragraphs = note
                              .split('\n')
                              .map((p) => p.trim())
                              .filter(Boolean);
                            return (
                              <TableRow key={i}>
                                <TableCell>
                                  <Typography style={{ fontSize: 20, fontWeight: 600, marginBottom: 10, color: '#9c27b0' }}>Note {i + 1}:</Typography>
                                  {paragraphs.map((p, idx) => {
                                    const heading = isHeading(p);
                                    return (
                                      <React.Fragment key={idx}>
                                        {heading && idx !== 0 && <Divider />}
                                        <Typography style={{ fontWeight: heading ? 600 : 400, color: heading ? '#1976d2' : '#333', marginBottom: 8, lineHeight: 1.6 }}>{p}</Typography>
                                      </React.Fragment>
                                    );
                                  })}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}
              </>
            )}

            {isOrganisation && (
              <>
                <Grid item md={6} xs={12}>
                  <Paper className={classes.table} style={{ overflow: 'hidden' }}>
                    <Title prefix="Key Information" className={classes.title} />

                    {matchRawResult && (
                      <>
                        {/* ── Header: avatar + name + badge ── */}
                        <Box display="flex" alignItems="center" style={{ padding: '16px', borderBottom: '1px solid rgba(0,0,0,0.08)', gap: 12 }}>
                          <Box
                            style={{
                              width: 44,
                              height: 44,
                              borderRadius: 8,
                              background: '#e3f2fd',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}>
                            <Typography style={{ fontWeight: 600, fontSize: 15, color: '#1976d2' }}>
                              {matchRawResult?.lists?.businessName
                                ?.split(/[\s,]+/)
                                .filter(Boolean)
                                .map((w) => w[0])
                                .slice(0, 2)
                                .join('')
                                .toUpperCase()}
                            </Typography>
                          </Box>
                          <Box flex={1}>
                            <Typography style={{ fontWeight: 600, fontSize: 15 }}>{matchRawResult?.lists?.businessName}</Typography>
                            <Typography style={{ fontSize: 13, opacity: 0.6 }}>{matchRawResult?.peps?.natureOfBusiness?.join(', ')}</Typography>
                          </Box>
                          <Box display="flex" flexWrap="wrap" justifyContent="flex-end" style={{ gap: 4 }}>
                            {matchRawResult?.aml_types?.map((aml, i) => (
                              <KeywordChip key={i} code={aml} classes={classes} />
                            ))}
                          </Box>
                        </Box>

                        {/* ── Business Information ── */}
                        <Box style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                          <Typography style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', opacity: 0.45, textTransform: 'uppercase', marginBottom: 10 }}>Business Information</Typography>
                          <Box display="flex" flexWrap="wrap" style={{ gap: '12px 24px' }}>
                            {[
                              { label: 'Business name', value: matchRawResult?.lists?.businessName },
                              { label: 'Country of incorporation', value: matchRawResult?.lists?.countryOfIncorporation },
                              {
                                label: 'Date of incorporation',
                                value: matchRawResult?.peps?.dayOfIncorporation ? `${matchRawResult.peps.dayOfIncorporation}-${matchRawResult.peps.monthOfIncorporation}-${matchRawResult.peps.yearOfIncorporation}` : null,
                              },
                              { label: 'Nature of business', value: matchRawResult?.peps?.natureOfBusiness?.join(', ') },
                            ].map(({ label, value }) => (
                              <Box key={label} style={{ minWidth: 140 }}>
                                <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 2 }}>{label}</Typography>
                                <Typography style={{ fontSize: 13, opacity: value ? 1 : 0.35 }}>{value || '—'}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>

                        {/* ── Identification Details ── */}
                        {matchRawResult?.peps?.identificationDetails?.length > 0 && (
                          <Box style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                            <Typography style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', opacity: 0.45, textTransform: 'uppercase', marginBottom: 10 }}>Identification</Typography>
                            <Box display="flex" flexWrap="wrap" style={{ gap: '12px 24px' }}>
                              {matchRawResult.peps.identificationDetails.map((id, i) => (
                                <Box key={i}>
                                  <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 2 }}>{id.type}</Typography>
                                  <Typography style={{ fontSize: 13, fontWeight: 600 }}>{id.value}</Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* ── Risk Classifications ── */}
                        {matchRawResult?.lists?.referenceList?.length > 0 && (
                          <Box style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                            <Typography style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', opacity: 0.45, textTransform: 'uppercase', marginBottom: 10 }}>Risk Classifications</Typography>
                            <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
                              {matchRawResult.lists.referenceList.map((ref, i) => {
                                const listItems = [
                                  ...(ref.sanctions || []).map((s) => ({ ...s, type: 'sanctions' })),
                                  ...(ref.otherOfficialList || []).map((o) => ({ ...o, type: 'ool' })),
                                  ...(ref.specialInterestPersonList || []).map((s) => ({ ...s, type: 'si' })),
                                ];
                                return (
                                  <Box
                                    key={i}
                                    style={{
                                      background: 'rgba(0,0,0,0.02)',
                                      border: '0.5px solid rgba(0,0,0,0.08)',
                                      borderRadius: 6,
                                      padding: '10px 12px',
                                    }}>
                                    <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: listItems.length > 0 ? 8 : 0 }}>
                                      <KeywordChip code={ref.keyword} classes={classes} />
                                      <Typography style={{ fontSize: 13, flex: 1 }}>{ref.category}</Typography>
                                      <Box
                                        style={{
                                          fontSize: 11,
                                          fontWeight: 600,
                                          padding: '2px 8px',
                                          borderRadius: 4,
                                          background: ref.status === 'ACTIVE' ? 'rgba(76,175,80,0.1)' : 'rgba(0,0,0,0.06)',
                                          color: ref.status === 'ACTIVE' ? '#2e7d32' : '#666',
                                        }}>
                                        {ref.status}
                                      </Box>
                                    </Box>

                                    {ref.subCategory && <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 6 }}>{ref.subCategory}</Typography>}

                                    {listItems.length > 0 && (
                                      <Box style={{ borderTop: '0.5px solid rgba(0,0,0,0.06)', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {listItems.map((item, j) => (
                                          <Box key={j} display="flex" alignItems="flex-start" style={{ gap: 8 }}>
                                            <Box style={{ width: 4, height: 4, borderRadius: '50%', background: '#999', marginTop: 6, flexShrink: 0 }} />
                                            <Box>
                                              <Typography style={{ fontSize: 12, fontWeight: 600 }}>{item.name}</Typography>
                                              {item.from?.year && (
                                                <Typography style={{ fontSize: 11, opacity: 0.5 }}>
                                                  Since {[item.from.day, item.from.month, item.from.year].filter(Boolean).join('/')}
                                                  {item.to ? ` – ${[item.to.day, item.to.month, item.to.year].filter(Boolean).join('/')}` : ' – present'}
                                                </Typography>
                                              )}
                                              {item.sources?.length > 0 && (
                                                <Box mt={0.5}>
                                                  {item.sources.map((src, k) => {
                                                    const isString = typeof src === 'string';
                                                    const url = isString ? src : src.sourceName;
                                                    const label = isString ? src : [src.sourceName, src.sourceDate, src.sourceLanguage].filter(Boolean).join(' · ');
                                                    return url ? (
                                                      <a key={k} href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, display: 'block', wordBreak: 'break-all', color: '#1976d2' }}>
                                                        {label}
                                                      </a>
                                                    ) : (
                                                      <span key={k} style={{ fontSize: 11, display: 'block', color: '#666' }}>
                                                        {label}
                                                      </span>
                                                    );
                                                  })}
                                                </Box>
                                              )}
                                            </Box>
                                          </Box>
                                        ))}
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        )}
                      </>
                    )}
                  </Paper>
                </Grid>

                {/* ── CONNECTIONS / ASSOCIATES ── */}
                {(matchRawResult?.lists?.connections?.length > 0 || matchRawResult?.lists?.linkedIndividuals?.length > 0 || matchRawResult?.lists?.linkedBusinesses?.length > 0) && (
                  <Grid item xs={12}>
                    <Paper className={classes.table} style={{ overflow: 'hidden' }}>
                      <Title prefix="Connections & Associates" className={classes.title} />
                      <Grid container spacing={2} style={{ padding: '14px 16px' }}>
                        {matchRawResult?.lists?.connections?.length > 0 && (
                          <Grid item xs={12}>
                            <ConnectionsTable title="Connections" rows={matchRawResult.lists.connections} classes={classes} />
                          </Grid>
                        )}
                        {matchRawResult?.lists?.linkedIndividuals?.length > 0 && (
                          <Grid item xs={12}>
                            <ConnectionsTable title="Linked Individuals" rows={matchRawResult.lists.linkedIndividuals} classes={classes} />
                          </Grid>
                        )}
                        {matchRawResult?.lists?.linkedBusinesses?.length > 0 && (
                          <Grid item xs={12}>
                            <ConnectionsTable title="Linked Businesses" rows={matchRawResult.lists.linkedBusinesses} classes={classes} />
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>
                )}

                {/* ── MEDIA ── */}
                {matchRawResult?.media?.length > 0 && (
                  <Grid item xs={12}>
                    <TableContainer component={Paper} className={classes.table}>
                      <Title prefix="Media" className={classes.title} />
                      <Table>
                        <TableBody>
                          {matchRawResult.media.map((item, i) => (
                            <TableRow key={i}>
                              <TableCell>
                                <Link href={item.externalLink} target="_blank" rel="noopener noreferrer">
                                  {item.title || item.externalLink}
                                </Link>
                                {item.title && (
                                  <Typography variant="caption" display="block" style={{ color: 'gray' }}>
                                    {item.externalLink}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}

                {/* ── PROFILE NOTES ── */}
                {matchRawResult?.peps?.profileNotes?.length > 0 && (
                  <Grid item xs={12}>
                    <TableContainer component={Paper} className={classes.table}>
                      <Title prefix="Profile Notes" className={classes.title} />
                      <Table>
                        <TableBody>
                          {matchRawResult.peps.profileNotes.map((note, i) => {
                            const paragraphs = note
                              .split('\n')
                              .map((p) => p.trim())
                              .filter(Boolean);
                            return (
                              <TableRow key={i}>
                                <TableCell>
                                  <Typography style={{ fontSize: 20, fontWeight: 600, marginBottom: 10, color: '#9c27b0' }}>Note {i + 1}:</Typography>
                                  {paragraphs.map((p, idx) => {
                                    const heading = isHeading(p);
                                    return (
                                      <React.Fragment key={idx}>
                                        {heading && idx !== 0 && <Divider />}
                                        <Typography style={{ fontWeight: heading ? 600 : 400, color: heading ? '#1976d2' : '#333', marginBottom: 8, lineHeight: 1.6 }}>{p}</Typography>
                                      </React.Fragment>
                                    );
                                  })}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </>
      )}
      {printPreview && (
        <DownloadRegtankPreview
          matchList={matchList}
          cancelPrint={() => setPrintPreview(false)}
          detail={matching}
          profile={{
            details: matchRawResult,
            names: matchRawResult?.detail?.profile?.person,
          }}
          isOrganisation={isOrganisation}
        />
      )}
    </Box>
  );
}

export default MatchDetailRegtank;
